import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import AdminLogin from "@/components/AdminLogin";

const FORMAT_DATE = (v) => new Date(v).toLocaleString();

const SIGNUP_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "interest", label: "Interest" },
  { key: "created_at", label: "When", format: FORMAT_DATE },
];

const COMMENT_COLUMNS = [
  { key: "chapter_id", label: "Chapter" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "comment", label: "Reflection" },
  { key: "created_at", label: "When", format: FORMAT_DATE },
];

const CONTACT_COLUMNS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "message", label: "Message" },
  { key: "created_at", label: "When", format: FORMAT_DATE },
];

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [signups, setSignups] = useState([]);
  const [comments, setComments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadAll = async (pwd) => {
    setLoading(true);
    try {
      const [s, c, co] = await Promise.all([
        api.get("/admin/signups", { headers: { "X-Admin-Password": pwd } }),
        api.get("/admin/comments", { headers: { "X-Admin-Password": pwd } }),
        api.get("/admin/contacts", { headers: { "X-Admin-Password": pwd } }),
      ]);
      setSignups(s.data || []);
      setComments(c.data || []);
      setContacts(co.data || []);
    } catch (err) {
      toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/login", { password });
      setAuthed(true);
      toast.success("Welcome back, Professor.");
      loadAll(password);
    } catch (err) {
      toast.error("Wrong password.");
    }
  };

  const logout = () => {
    setAuthed(false);
    setPassword("");
    setSignups([]); setComments([]); setContacts([]);
  };

  if (!authed) {
    return (
      <AdminLogin
        password={password}
        onPasswordChange={setPassword}
        onSubmit={login}
      />
    );
  }

  return (
    <main data-testid="admin-dashboard-view" className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="font-ui text-[11px] uppercase tracking-[0.3em] text-primary">Admin</p>
          <h1 className="font-display text-4xl text-foreground mt-2">The quiet room</h1>
          <p className="font-body text-muted-foreground mt-1">Signups, reflections and letters.</p>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout" className="rounded-full font-ui">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>

      <Tabs defaultValue="signups" className="w-full">
        <TabsList className="bg-muted">
          <TabsTrigger value="signups" data-testid="tab-signups" className="font-ui">
            Signups <span className="ml-2 text-xs opacity-70">({signups.length})</span>
          </TabsTrigger>
          <TabsTrigger value="comments" data-testid="tab-comments" className="font-ui">
            Reflections <span className="ml-2 text-xs opacity-70">({comments.length})</span>
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts" className="font-ui">
            Letters <span className="ml-2 text-xs opacity-70">({contacts.length})</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="signups">
          <DataTable rows={signups} columns={SIGNUP_COLUMNS} empty="No signups yet." />
        </TabsContent>
        <TabsContent value="comments">
          <DataTable rows={comments} columns={COMMENT_COLUMNS} empty="No reflections yet." />
        </TabsContent>
        <TabsContent value="contacts">
          <DataTable rows={contacts} columns={CONTACT_COLUMNS} empty="No letters yet." />
        </TabsContent>
      </Tabs>
      {loading && <p className="mt-6 font-ui text-sm text-muted-foreground">Loading…</p>}
    </main>
  );
}

function DataTable({ rows, columns, empty }) {
  if (!rows || rows.length === 0) {
    return <p className="mt-8 font-body italic text-muted-foreground">{empty}</p>;
  }
  return (
    <div className="mt-6 overflow-x-auto border border-border rounded-2xl bg-card">
      <table className="w-full text-left">
        <thead className="bg-muted/60">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="font-ui text-xs uppercase tracking-[0.2em] text-muted-foreground px-4 py-3">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-border align-top" data-testid={`admin-row-${r.id || i}`}>
              {columns.map((c) => (
                <td key={c.key} className="font-body text-sm text-foreground/90 px-4 py-4 max-w-md whitespace-pre-wrap break-words">
                  {c.format ? c.format(r[c.key]) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
